import { Link, Stack, Typography } from "@mui/material";
import VidletLogo from "components/VidletLogo";
import { useSearchParams } from "react-router-dom";
import { flexCentered } from "theme/base/flex";

const APPLE_APP_STORE_URL =
  "https://apps.apple.com/us/app/vidlet-3-0/id1604713113";
const GOOGLE_PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.vidlet.mobile";

const ERROR_MESSAGES: Record<string, { title: string; body: string }> = {
  expired: {
    title: "This link has expired",
    body: "Your confirmation link has expired. Please submit the signup form again to receive a new email.",
  },
  already_used: {
    title: "This link was already used",
    body: "This confirmation link has already been used. If you need help accessing the panel, contact us below.",
  },
  not_found: {
    title: "This link is not valid",
    body: "We could not find a matching confirmation. Please check the link in your email or submit the signup form again.",
  },
  not_configured: {
    title: "Signup is temporarily unavailable",
    body: "Panel signup is not fully configured in this environment. Please try again later or contact us for help.",
  },
  email_not_found: {
    title: "We could not confirm your email",
    body: "No email was found for this signup. Please submit the form again with a valid email address.",
  },
  script_expired: {
    title: "Signup is temporarily unavailable",
    body: "The welcome study for new panel members is no longer active. Please contact us and we will help you get set up.",
  },
  bad_request: {
    title: "Something went wrong",
    body: "We could not complete your confirmation. Please try again or contact us for help.",
  },
  unknown: {
    title: "Something went wrong",
    body: "We could not complete your confirmation. Please try again or contact us for help.",
  },
};

function getErrorContent(code: string | null) {
  if (!code) {
    return null;
  }
  return ERROR_MESSAGES[code] ?? ERROR_MESSAGES.unknown;
}

export default function PanelSignup() {
  const [searchParams] = useSearchParams();
  const errorCode = searchParams.get("code");
  const errorContent = getErrorContent(errorCode);

  return (
    <Stack
      sx={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: "background.default",
      }}
    >
      <Stack
        sx={{
          height: "5rem",
          minHeight: "5rem",
          width: "100%",
          backgroundColor: "white",
          ...flexCentered,
        }}
      >
        <VidletLogo sx={{ fontSize: "3.5rem" }} />
      </Stack>
      <Stack
        component="main"
        spacing={2}
        sx={{
          flex: 1,
          px: 3,
          py: 6,
          maxWidth: 640,
          mx: "auto",
          textAlign: "center",
        }}
      >
        {errorContent ? (
          <>
            <Typography variant="h4" component="h1">
              {errorContent.title}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {errorContent.body}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
              Questions? Email{" "}
              <Link href="mailto:info@vidlet.com">info@vidlet.com</Link>.
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="h4" component="h1">
              You&apos;re signed up
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Thanks for confirming your email. You&apos;re on the Vidlet
              research panel. Watch for study invitations by email, and download
              the mobile app when you&apos;re ready to record responses.
            </Typography>
            <Stack spacing={0.5} sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Get Vidlet 3.0
              </Typography>
              <Typography variant="body1">
                <Link
                  href={APPLE_APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Apple App Store
                </Link>
                {" · "}
                <Link
                  href={GOOGLE_PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Google Play
                </Link>
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
              Questions? Email{" "}
              <Link href="mailto:info@vidlet.com">info@vidlet.com</Link>.
            </Typography>
          </>
        )}
      </Stack>
    </Stack>
  );
}
